import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { Ban, Cable, Check, CheckCircle2, Copy, Eye, EyeOff, Folder, Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { SettingsLayout, type SettingsTabKey } from "@/components/settings/SettingsLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Radio } from "@/components/ui/radio"
import { DropboxFolderPicker } from "@/components/clips/DropboxFolderPicker"
import type { ClipTypeState, DropboxIntegrationState } from "@/components/clips/types"
import type { PageProps } from "@/types/inertia"

const PLACEHOLDER_TABS = ["api", "dropbox", "clip_types"] as const
type PlaceholderTab = (typeof PLACEHOLDER_TABS)[number]

type ApiToken = {
  id: number
  name: string
  token: string
  last_used_at: string | null
  revoked_at: string | null
  created_at: string
}

type SettingsProps = PageProps<{
  dropbox?: DropboxIntegrationState
  api_tokens?: ApiToken[]
  clip_types?: ClipTypeState[]
}>

function tabFromUrl(url: string): PlaceholderTab {
  const query = url.split("?")[1] ?? ""
  const params = new URLSearchParams(query)
  const tab = params.get("tab")
  return (PLACEHOLDER_TABS as readonly string[]).includes(tab ?? "") ? (tab as PlaceholderTab) : "api"
}

export default function Settings() {
  const { url, props } = usePage<SettingsProps>()
  const activeTab: SettingsTabKey = tabFromUrl(url)

  return (
    <>
      <Head title="Settings">
        <meta name="description" content="Manage your Tubery account and integrations." />
        <meta property="og:title" content="Settings" />
        <meta property="og:description" content="Manage your Tubery account and integrations." />
      </Head>
      <AppShell>
        <SettingsLayout activeTab={activeTab}>
          {activeTab === "api" && (
            <ApiTab tokens={props.api_tokens ?? []} />
          )}
          {activeTab === "dropbox" && <DropboxTab dropbox={props.dropbox ?? null} />}
          {activeTab === "clip_types" && (
            <ClipTypesTab clipTypes={props.clip_types ?? []} dropbox={props.dropbox ?? null} />
          )}
        </SettingsLayout>
      </AppShell>
    </>
  )
}

function ApiTab({ tokens }: { tokens: ApiToken[] }) {
  const [name, setName] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setSubmitting(true)
    router.post(
      "/settings/api-tokens",
      { api_token: { name: trimmed } },
      {
        preserveScroll: true,
        onSuccess: () => setName(""),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleRevoke(token: ApiToken) {
    if (!window.confirm(`Revoke "${token.name}"? Any agent using this token will lose access immediately.`)) {
      return
    }
    router.patch(`/settings/api-tokens/${token.id}/revoke`, {}, { preserveScroll: true })
  }

  function handleDelete(token: ApiToken) {
    if (!window.confirm(`Delete "${token.name}"? This permanently removes the token.`)) {
      return
    }
    router.delete(`/settings/api-tokens/${token.id}`, { preserveScroll: true })
  }

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h2>API tokens</h2>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Bearer tokens for external agent skills calling <code>/api/*</code>. Send the token in the
          <code className="mx-1">Authorization</code> header as{" "}
          <code>Token token="&lt;token&gt;"</code>. See <a href="/api/docs">/api/docs</a> for endpoints.
        </p>

        <form onSubmit={handleCreate} className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-start">
          <div className="flex-1">
            <label htmlFor="api-token-name" className="sr-only">
              Token name
            </label>
            <Input
              id="api-token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Token name (e.g. Production scheduler, Local agent)"
              maxLength={60}
              autoComplete="off"
            />
          </div>
          <Button type="submit" disabled={submitting || !name.trim()}>
            <Plus className="mr-1 h-4 w-4" /> Generate token
          </Button>
        </form>
      </section>

      <section>
        <h2>Existing tokens</h2>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Click the eye icon to reveal a token, or the copy icon to put it on your clipboard.
          Revoking a token disables authentication while keeping the row so you can see what's
          been turned off. Delete removes it permanently.
        </p>

        <div className="mt-4">
          {tokens.length === 0 ? (
            <p className="text-sm text-ink-muted">No tokens yet. Generate one above to get started.</p>
          ) : (
            <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {tokens.map((token) => (
                <ApiTokenRow
                  key={token.id}
                  token={token}
                  onRevoke={handleRevoke}
                  onDelete={handleDelete}
                />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  )
}

function maskToken(plaintext: string): string {
  // Show the prefix + first 4 random chars + the last 4 chars, with the
  // middle replaced by dots. Lets the user tell tokens apart at a glance
  // without revealing the whole thing.
  if (plaintext.length <= 14) return "•".repeat(plaintext.length)
  return `${plaintext.slice(0, 11)}${"•".repeat(8)}${plaintext.slice(-4)}`
}

function ApiTokenRow({
  token,
  onRevoke,
  onDelete,
}: {
  token: ApiToken
  onRevoke: (t: ApiToken) => void
  onDelete: (t: ApiToken) => void
}) {
  const [revealed, setRevealed] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const created = new Date(token.created_at).toLocaleString()
  const lastUsed = token.last_used_at
    ? new Date(token.last_used_at).toLocaleString()
    : null
  const revokedAt = token.revoked_at
    ? new Date(token.revoked_at).toLocaleString()
    : null

  function handleCopy() {
    navigator.clipboard.writeText(token.token).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <li>
      <div className="flex flex-col gap-2 px-4 py-3 hover:bg-surface sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-ink-display">{token.name}</span>
            {token.revoked_at && (
              <span className="inline-flex items-center gap-1 rounded-md border border-hairline bg-surface px-1.5 py-0.5 text-xs text-ink-muted">
                <Ban className="h-3 w-3" /> Revoked
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1">
            <code className="overflow-x-auto font-mono text-xs text-ink-body">
              {revealed ? token.token : maskToken(token.token)}
            </code>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRevealed((r) => !r)}
              aria-label={revealed ? `Hide ${token.name}` : `Reveal ${token.name}`}
              title={revealed ? "Hide" : "Reveal"}
            >
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              aria-label={`Copy ${token.name}`}
              title="Copy"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-1 text-xs text-ink-muted">
            Created {created}
            {" · "}
            {lastUsed ? `Last used ${lastUsed}` : "Never used"}
            {revokedAt && ` · Revoked ${revokedAt}`}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {!token.revoked_at && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRevoke(token)}
              aria-label={`Revoke ${token.name}`}
            >
              <Ban className="mr-1 h-4 w-4" /> Revoke
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(token)}
            aria-label={`Delete ${token.name}`}
          >
            <Trash2 className="mr-1 h-4 w-4" /> Delete
          </Button>
        </div>
      </div>
    </li>
  )
}

function DropboxTab({ dropbox }: { dropbox: DropboxIntegrationState | null }) {
  const state = dropbox ?? {
    connected: false,
    configured: false,
    account_email: null,
    broll_root_path: null,
    deprecated_folder_names: [],
    default_deprecated_folder_name: "",
    ignored_folder_names: [],
    last_synced_at: null,
    last_sync_outcome: null,
    last_sync_details: null,
  }
  const [pickerOpen, setPickerOpen] = React.useState(false)

  function handleDisconnect() {
    if (!window.confirm("Disconnect Dropbox? Clips already in Tubery remain visible but new syncs won't run.")) return
    router.delete("/settings/dropbox", { preserveScroll: true })
  }

  function handleSync() {
    router.post("/clips/trigger_sync", {}, { preserveScroll: true })
  }

  return (
    <div className="flex flex-col gap-4">
      <article className="rounded-lg border border-hairline bg-surface p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2>Dropbox integration</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Tubery mirrors a Dropbox-backed B-roll archive. Connect Dropbox and pick a root folder to start syncing.
            </p>
          </div>
          {state.connected ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-faded px-2 py-0.5 text-xs font-medium text-accent-display">
              <CheckCircle2 className="h-3 w-3" /> Connected
            </span>
          ) : null}
        </div>

        {state.connected ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-page px-3 py-2 text-sm">
              <span className="text-ink-muted">Account:</span>
              <span className="font-medium text-ink-display">{state.account_email ?? "(unknown)"}</span>
            </div>

            <div className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-page px-3 py-2 text-sm">
              <span className="text-ink-muted">B-roll root:</span>
              <span className="font-mono text-ink-display">{state.broll_root_path ?? "(not set — pick one below)"}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                <Folder className="mr-1 h-4 w-4" />
                {state.broll_root_path ? "Change B-roll root" : "Pick B-roll root"}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleSync} disabled={!state.configured}>
                <RefreshCw className="mr-1 h-4 w-4" /> Sync now
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDisconnect}>Disconnect</Button>
            </div>

            {state.last_synced_at && (
              <p className="text-xs text-ink-muted">
                Last sync: {new Date(state.last_synced_at).toLocaleString()} — outcome <code>{state.last_sync_outcome}</code>.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-4">
            <Button asChild>
              {/* Plain <a>, not Inertia's <Link>: Inertia intercepts <Link>
                  clicks and uses XHR, which can't follow a 302 to an external
                  origin (dropbox.com). A real browser navigation is required. */}
              <a href="/auth/dropbox" className="no-underline">
                <Cable className="mr-1 h-4 w-4" /> Connect Dropbox
              </a>
            </Button>
          </div>
        )}
      </article>

      {state.connected ? (
        <DeprecatedFoldersPanel
          initialNames={state.deprecated_folder_names}
          initialDefaultName={state.default_deprecated_folder_name}
        />
      ) : null}

      {state.connected ? (
        <IgnoredFoldersPanel initialNames={state.ignored_folder_names} />
      ) : null}

      <DropboxFolderPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialPath={state.broll_root_path ?? ""}
        title="Pick B-roll root folder"
        ctaLabel="Use as B-roll root"
        onPick={({ path, id }) => {
          router.patch("/settings/dropbox/root", { broll_root_path: path, broll_root_dropbox_id: id ?? null }, {
            preserveScroll: false,
          })
        }}
      />
    </div>
  )
}

function normalizeFolderName(value: string): string {
  return value.trim().toLowerCase().replace(/\//g, "")
}

function DeprecatedFoldersPanel({
  initialNames,
  initialDefaultName,
}: {
  initialNames: string[]
  initialDefaultName: string
}) {
  const [names, setNames] = React.useState<string[]>(initialNames)
  const [defaultName, setDefaultName] = React.useState<string>(initialDefaultName)
  const [draft, setDraft] = React.useState<string>("")
  const [error, setError] = React.useState<string | null>(null)

  // Reset local state if the server-side value changes (e.g. after a save +
  // Inertia partial reload).
  React.useEffect(() => {
    setNames(initialNames)
    setDefaultName(initialDefaultName)
  }, [initialNames, initialDefaultName])

  const dirty =
    JSON.stringify(names) !== JSON.stringify(initialNames) ||
    defaultName !== initialDefaultName

  function handleAdd() {
    setError(null)
    const cleaned = normalizeFolderName(draft)
    if (!cleaned) {
      setError("Enter a folder name.")
      return
    }
    if (names.includes(cleaned)) {
      setError(`"${cleaned}" is already in the list.`)
      return
    }
    setNames([...names, cleaned])
    setDraft("")
  }

  function handleRemove(name: string) {
    setError(null)
    if (names.length === 1) {
      setError("At least one folder name is required.")
      return
    }
    const next = names.filter((n) => n !== name)
    setNames(next)
    if (defaultName === name) {
      setDefaultName(next[0])
    }
  }

  function handleSave() {
    setError(null)
    if (names.length === 0) {
      setError("At least one folder name is required.")
      return
    }
    if (!names.includes(defaultName)) {
      setError("Default folder must be one of the configured names.")
      return
    }
    router.patch(
      "/settings/dropbox/deprecated_folders",
      { names, default_name: defaultName },
      { preserveScroll: true },
    )
  }

  return (
    <article className="rounded-lg border border-hairline bg-surface p-6">
      <div>
        <h2>Deprecated folder names</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Folders whose name matches any of these will automatically mark clips inside
          them as deprecated — at any depth in your B-roll tree. Matching is
          case-insensitive (<code>_Deprecated</code> matches <code>_deprecated</code>).
          The default name is used when Tubery creates a deprecated subfolder for you.
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {names.map((name) => (
          <div
            key={name}
            className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-page px-3 py-2"
          >
            <label className="flex flex-1 items-center gap-3 font-normal text-ink-body">
              <Radio
                name="default_deprecated_folder_name"
                value={name}
                checked={defaultName === name}
                onChange={() => setDefaultName(name)}
              />
              <span className="font-mono text-ink-display">{name}</span>
              {defaultName === name && (
                <span className="rounded-md border border-hairline bg-surface px-1.5 py-0.5 text-xs text-ink-muted">
                  Default
                </span>
              )}
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemove(name)}
              aria-label={`Remove ${name}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a folder name (e.g. _trash)"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              handleAdd()
            }
          }}
        />
        <Button variant="secondary" size="sm" onClick={handleAdd}>
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>

      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={!dirty}>
          Save changes
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setNames(initialNames)
              setDefaultName(initialDefaultName)
              setError(null)
            }}
          >
            Reset
          </Button>
        )}
      </div>
    </article>
  )
}

// Converts an absolute Dropbox path from the folder picker into a path
// relative to the B-roll root (what ClipType.folder_path stores). Returns
// null when the picked folder isn't strictly inside the root.
function relativizeToBrollRoot(absolutePath: string, rootPath: string | null): string | null {
  if (!rootPath) return null
  const root = rootPath.replace(/\/+$/, "")
  if (!absolutePath.toLowerCase().startsWith(`${root.toLowerCase()}/`)) return null
  return absolutePath.slice(root.length)
}

function ClipTypesTab({
  clipTypes,
  dropbox,
}: {
  clipTypes: ClipTypeState[]
  dropbox: DropboxIntegrationState | null
}) {
  const configured = dropbox?.configured ?? false

  return (
    <div className="flex flex-col gap-4">
      <article className="rounded-lg border border-hairline bg-surface p-6">
        <h2>Clip types</h2>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Each clip type maps to a folder under your B-roll root (e.g. <code>/Cam recordings</code>).
          Every clip living at or below that folder is automatically assigned the type — and moving a
          clip into a different type's folder reassigns it on the next sync. Each type folder can have
          its own <code>_inbox</code> for new clips awaiting enrichment. A clip belongs to one type at most.
        </p>

        {!configured && (
          <p className="mt-4 text-sm text-ink-muted">
            Connect Dropbox and pick a B-roll root (Settings → Dropbox) before defining clip types.
          </p>
        )}

        {configured && (
          <>
            <div className="mt-4 flex flex-col gap-2">
              {clipTypes.length === 0 && (
                <p className="text-sm text-ink-muted">No clip types defined yet. Add one below.</p>
              )}
              {clipTypes.map((clipType) => (
                <ClipTypeRow key={clipType.id} clipType={clipType} brollRootPath={dropbox?.broll_root_path ?? null} />
              ))}
            </div>

            <NewClipTypeForm brollRootPath={dropbox?.broll_root_path ?? null} />
          </>
        )}
      </article>
    </div>
  )
}

function ClipTypeRow({
  clipType,
  brollRootPath,
}: {
  clipType: ClipTypeState
  brollRootPath: string | null
}) {
  const [editing, setEditing] = React.useState(false)

  function handleDelete() {
    if (
      !window.confirm(
        `Delete clip type "${clipType.name}"? Clips keep their files and folders — they just lose the type assignment.`,
      )
    ) {
      return
    }
    router.delete(`/settings/clip-types/${clipType.id}`, { preserveScroll: true })
  }

  if (editing) {
    return (
      <ClipTypeEditor
        brollRootPath={brollRootPath}
        initialName={clipType.name}
        initialFolderPath={clipType.folder_path}
        submitLabel="Save"
        onCancel={() => setEditing(false)}
        onSubmit={({ name, folder_path }) => {
          router.patch(
            `/settings/clip-types/${clipType.id}`,
            { name, folder_path },
            { preserveScroll: true, onSuccess: () => setEditing(false) },
          )
        }}
      />
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-page px-3 py-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink-display">{clipType.name}</span>
          <span className="whitespace-nowrap rounded-md border border-hairline bg-surface px-1.5 py-0.5 text-xs text-ink-muted">
            {clipType.clip_count} clip{clipType.clip_count === 1 ? "" : "s"}
          </span>
        </div>
        <p className="mt-0.5 truncate font-mono text-xs text-ink-muted" title={clipType.folder_path}>
          {clipType.folder_path}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)} aria-label={`Edit ${clipType.name}`}>
          <Pencil className="mr-1 h-4 w-4" /> Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={handleDelete} aria-label={`Delete ${clipType.name}`}>
          <Trash2 className="mr-1 h-4 w-4" /> Delete
        </Button>
      </div>
    </div>
  )
}

function NewClipTypeForm({ brollRootPath }: { brollRootPath: string | null }) {
  const [resetKey, setResetKey] = React.useState(0)

  return (
    <div className="mt-6 border-t border-hairline pt-4">
      <h3>Add a clip type</h3>
      <div className="mt-2">
        <ClipTypeEditor
          key={resetKey}
          brollRootPath={brollRootPath}
          submitLabel="Add clip type"
          onSubmit={({ name, folder_path }) => {
            router.post(
              "/settings/clip-types",
              { name, folder_path },
              { preserveScroll: true, onSuccess: () => setResetKey((k) => k + 1) },
            )
          }}
        />
      </div>
    </div>
  )
}

function ClipTypeEditor({
  brollRootPath,
  initialName = "",
  initialFolderPath = "",
  submitLabel,
  onSubmit,
  onCancel,
}: {
  brollRootPath: string | null
  initialName?: string
  initialFolderPath?: string
  submitLabel: string
  onSubmit: (values: { name: string; folder_path: string }) => void
  onCancel?: () => void
}) {
  const [name, setName] = React.useState(initialName)
  const [folderPath, setFolderPath] = React.useState(initialFolderPath)
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError("Name is required.")
      return
    }
    if (!folderPath) {
      setError("Pick the Dropbox folder this type maps to.")
      return
    }
    onSubmit({ name: name.trim(), folder_path: folderPath })
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-md border border-hairline bg-page px-3 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (e.g. Cam recordings)"
          maxLength={60}
          className="sm:max-w-56"
        />
        <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
          <Folder className="mr-1 h-4 w-4" />
          {folderPath ? "Change folder" : "Pick folder"}
        </Button>
        {folderPath && (
          <code className="truncate font-mono text-xs text-ink-body" title={folderPath}>
            {folderPath}
          </code>
        )}
        <div className="flex items-center gap-1 sm:ml-auto">
          <Button type="submit" size="sm">{submitLabel}</Button>
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

      <DropboxFolderPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialPath={`${brollRootPath ?? ""}${folderPath}`}
        title="Pick the clip type's folder"
        ctaLabel="Use this folder"
        onPick={({ path }) => {
          const relative = relativizeToBrollRoot(path, brollRootPath)
          if (!relative) {
            setError(`Pick a folder inside the B-roll root (${brollRootPath ?? "not set"}).`)
            return
          }
          setError(null)
          setFolderPath(relative)
        }}
      />
    </form>
  )
}

function IgnoredFoldersPanel({ initialNames }: { initialNames: string[] }) {
  const [names, setNames] = React.useState<string[]>(initialNames)
  const [draft, setDraft] = React.useState<string>("")
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    setNames(initialNames)
  }, [initialNames])

  const dirty = JSON.stringify(names) !== JSON.stringify(initialNames)

  function handleAdd() {
    setError(null)
    const cleaned = normalizeFolderName(draft)
    if (!cleaned) {
      setError("Enter a folder name.")
      return
    }
    if (names.includes(cleaned)) {
      setError(`"${cleaned}" is already in the list.`)
      return
    }
    setNames([...names, cleaned])
    setDraft("")
  }

  function handleRemove(name: string) {
    setError(null)
    setNames(names.filter((n) => n !== name))
  }

  function handleSave() {
    router.patch(
      "/settings/dropbox/ignored_folders",
      { names },
      { preserveScroll: true },
    )
  }

  return (
    <article className="rounded-lg border border-hairline bg-surface p-6">
      <div>
        <h2>Ignored folders</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Folders Tubery will skip entirely during sync — at any depth in your B-roll
          tree. Files inside them won't create clips. Matching is case-insensitive
          (<code>Proxy</code> matches <code>proxy</code>). Existing clips already
          tracked but living in an ignored path keep their last-known state until
          you remove or deprecate them manually.
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {names.length === 0 && (
          <p className="text-sm text-ink-muted">No folders are being ignored.</p>
        )}
        {names.map((name) => (
          <div
            key={name}
            className="flex items-center justify-between gap-2 rounded-md border border-hairline bg-page px-3 py-2"
          >
            <span className="font-mono text-ink-display">{name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemove(name)}
              aria-label={`Remove ${name}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a folder name (e.g. raw, _archive_full)"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              handleAdd()
            }
          }}
        />
        <Button variant="secondary" size="sm" onClick={handleAdd}>
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>

      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={!dirty}>
          Save changes
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setNames(initialNames)
              setError(null)
            }}
          >
            Reset
          </Button>
        )}
      </div>
    </article>
  )
}
